import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Button, Grid, Chip, Box, Typography
} from '@mui/material';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import { useAuth } from './AuthContext';
import { STAGES } from './constants';
import { nowISO } from './helpers';
import { useTags, TAG_COLORS } from './useTags';

export default function ContactModal({ initial, onClose }) {
  const { staff } = useAuth();
  const { tags, addTag } = useTags();

  const [f, setF] = useState({
    nome: '',
    azienda: '',
    telefono: '',
    email: '',
    citta: '',
    fonte: '',
    stage: 'nuovo',
    owner: staff?.nome || 'Mario',
    note: '',
    tags: Array.isArray(initial?.tags)
      ? initial.tags
      : initial?.tag
        ? [initial.tag]
        : [],
    ...initial
  });

  const [saving, setSaving] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(false);

  const up = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const selectedTags = Array.isArray(f.tags) ? f.tags : [];
  const availableTags = tags.filter(t => !selectedTags.includes(t.name));

  const getTagColor = (tagName) => {
    return tags.find(t => t.name === tagName)?.color || '#94a3b8';
  };

  const handleTagSelect = (tagName) => {
    up('tags', Array.from(new Set([...selectedTags, tagName])));
    setShowColorPicker(false);
  };

  const handleTagRemove = (tagName) => {
    up('tags', selectedTags.filter(t => t !== tagName));
  };

  const handleNewTag = async (color) => {
    const cleanName = newTagName.trim();
    if (!cleanName) return;

    const created = await addTag(cleanName, color);

    up('tags', Array.from(new Set([...selectedTags, created.name])));
    setNewTagName('');
    setShowColorPicker(false);
  };

  const submit = async () => {
    if (!f.nome?.trim()) return alert('Nome obbligatorio');

    setSaving(true);
    try {
      const normalizedTags = Array.from(
        new Set((f.tags || []).map(t => t.trim()).filter(Boolean))
      );

      const payload = {
        ...f,
        tags: normalizedTags,
        tag: normalizedTags[0] || '', // compatibilità temporanea con la schermata Contatti
        updatedAt: nowISO()
      };

      if (f.id) {
        const { id, ...data } = payload;
        await updateDoc(doc(db, 'crm_contatti', id), data);
      } else {
        await addDoc(collection(db, 'crm_contatti'), {
          ...payload,
          createdAt: nowISO()
        });
      }

      onClose();
    } catch (e) {
      alert('Errore salvataggio: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{f.id ? 'Modifica contatto' : 'Nuovo contatto'}</DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Nome *"
              value={f.nome}
              onChange={e => up('nome', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Azienda"
              value={f.azienda}
              onChange={e => up('azienda', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Telefono"
              value={f.telefono}
              onChange={e => up('telefono', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              value={f.email}
              onChange={e => up('email', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Città"
              value={f.citta}
              onChange={e => up('citta', e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Fonte"
              value={f.fonte}
              onChange={e => up('fonte', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}
                >
                  Tag disponibili
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {availableTags.map(t => (
                    <Chip
                      key={t.id}
                      label={t.name}
                      size="small"
                      clickable
                      onClick={() => handleTagSelect(t.name)}
                      sx={{
                        bgcolor: 'transparent',
                        color: t.color,
                        border: 1,
                        borderColor: t.color,
                        fontWeight: 500
                      }}
                    />
                  ))}

                  <Chip
                    label="+ Nuovo tag"
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    sx={{ borderStyle: 'dashed' }}
                  />
                </Box>

                {showColorPicker && (
                  <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nome tag"
                      value={newTagName}
                      onChange={e => setNewTagName(e.target.value)}
                      sx={{ mb: 1 }}
                      placeholder="Es: Noleggio, Soccorso, Trasporti..."
                    />
                    <Typography variant="caption" color="text.secondary">
                      Scegli colore:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {TAG_COLORS.map(c => (
                        <Box
                          key={c}
                          onClick={() => handleNewTag(c)}
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            bgcolor: c,
                            cursor: 'pointer',
                            border: 2,
                            borderColor: 'transparent',
                            '&:hover': {
                              borderColor: 'grey.800',
                              transform: 'scale(1.15)'
                            },
                            transition: 'all 0.15s'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>

              <Box sx={{ pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}
                >
                  Tag selezionati per questo contatto
                </Typography>

                {selectedTags.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessun tag selezionato
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selectedTags.map(tagName => (
                      <Chip
                        key={tagName}
                        label={tagName}
                        size="small"
                        onDelete={() => handleTagRemove(tagName)}
                        sx={{
                          bgcolor: getTagColor(tagName),
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              size="small"
              label="Stage"
              value={f.stage}
              onChange={e => up('stage', e.target.value)}
            >
              {STAGES.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Owner"
              value={f.owner}
              onChange={e => up('owner', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Note"
              value={f.note}
              onChange={e => up('note', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Salvo...' : 'Salva'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}